import React, { Fragment } from 'react';
import { TableRow, TableCell, IconButton } from '@mui/material';
import { colors, spacing, radius } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarIconSvg from 'assets/personal_zone/Calendar.svg';
import CouponTable from './CouponTable';
import { BenefitCell } from './BenefitCell';
import { generateTestId, urlEncodeString } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
import { useHistory } from 'react-router-dom';
import * as routes from 'constants/routes';

function BenefitTableRow({ benefit, testID, actionsBaseTestID: externalActionsBaseTestID, isExpanded, onToggle, onArchive, onCancel, activeCancelCouponId, isPaidCoupons = true, activeFilters, onCouponStatusUpdate, onCouponArchiveUpdate }) {
    const { translate } = useTranslations();
    const history = useHistory();

    const openBenefitScreen = (benefit) => {
        if(!benefit.unavailable) {
            history.push(`${routes.benefit}/${benefit.id}/${benefit.slug || urlEncodeString(benefit.name)}`);
        }
    }

    const rowStyle = {
        backgroundColor: colors.palette.white,
        '& td': {
            border: 'none',
            paddingY: `${spacing.xxl}px`,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            verticalAlign: 'middle'
        },
        '& td:first-of-type': {
            borderTopRightRadius: `${radius.lg}px`,
            borderBottomRightRadius: isExpanded ? '0' : `${radius.lg}px`,
        },
        '& td:last-of-type': {
            borderTopLeftRadius: `${radius.lg}px`,
            borderBottomLeftRadius: isExpanded ? '0' : `${radius.lg}px`,
        },
    };

    return (
        <Fragment>
            <TableRow testId={generateTestId(testID, 'benefit-row')} sx={rowStyle}>
                <TableCell testId={generateTestId(testID, 'toggle-cell')} sx={{ width: '50px' }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        testId={generateTestId(testID, 'toggle-btn')}
                        onClick={onToggle}
                    >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                
                {isPaidCoupons && (
                    <TableCell testId={generateTestId(testID, 'savings-cell')} align="left" sx={{ minWidth: '120px' }}>
                        <TextHot 
                            testID={generateTestId(testID, 'savings')}
                            size="xxl" 
                            color="primary"
                        >
                        ₪{Math.round(Number(benefit.totalSavings))}
                        </TextHot>
                    </TableCell>
                )}

                <TableCell testId={generateTestId(testID, 'date-cell')} align="left" sx={{ minWidth: '120px' }}>
                    <TextHot 
                        testID={generateTestId(testID, 'date')}
                        size="xxl" 
                        color="primary"
                    >
                        <BenefitCell name={benefit.latest_order_date} imagePath={CalendarIconSvg} />
                    </TextHot>
                </TableCell>

                <TableCell testId={generateTestId(testID, 'order-count-cell')} align="left" sx={{ direction: 'ltr', minWidth: '120px' }}>
                    <TextHot 
                        testID={generateTestId(testID, 'order-count')}
                        size="xxl" 
                        color="primary"
                    >
                        {benefit.count} {translate('my_coupons_orders_count_label')}
                    </TextHot>
                </TableCell>

                <TableCell testId={generateTestId(testID, 'benefit-name-cell')} onClick={() => openBenefitScreen(benefit)} align="left">
                    <BenefitCell 
                        name={benefit.name} 
                        imagePath={benefit.image} 
                        isBold={true} 
                        imageSize={50} 
                        gap={spacing.md}
                    />
                </TableCell>
            </TableRow>
            
            <CouponTable 
                benefit={benefit} 
                testID={generateTestId(testID, 'coupon-table')} 
                actionsBaseTestID={externalActionsBaseTestID}
                isExpanded={isExpanded} 
                onArchive={onArchive} 
                onCancel={onCancel} 
                activeCancelCouponId={activeCancelCouponId} 
                isPaidCoupons={isPaidCoupons} 
                activeFilters={activeFilters}
                onCouponStatusUpdate={onCouponStatusUpdate}
                onCouponArchiveUpdate={onCouponArchiveUpdate}
            />

            {isExpanded && (
                <TableRow testId={generateTestId(testID, 'spacer-row')} sx={{ height: `${spacing.xs}px`, backgroundColor: 'transparent' }}>
                    <TableCell colSpan={7} sx={{ border: 'none', p: 0 }} />
                </TableRow>
            )}
        </Fragment>
    );
}

export default BenefitTableRow;